import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import {
  AccountingPeriodClose,
  AccountLedgerEntry,
  AccountLedgerRequest,
  AccountTotalsRequest,
  AccountTotalsRow,
  AvailableAccountLedgerRangeRequest,
  CreateAccountingPeriodCloseRequest,
  GeneralLedgerAccount,
  GeneralLedgerAccountRequest,
  GeneralLedgerAccountType,
  ItemGeneralLedgerAccount,
  ItemGeneralLedgerTransactionType,
  ItemType,
  PeriodicLedgerTotal,
  PeriodicLedgerTotalsRequest,
  UpsertItemGeneralLedgerAccountRequest,
} from './accounting';
import { handlePaginatedRequest, handleRequest } from '../quipli-service-client';
import { DateTimeRange } from '../report/report';
import { AxiosRequestConfig } from 'axios/index';
import { DateTime } from 'luxon';
import { Pagination } from '../pagination/pagination';
import { AccountingParsers } from './accounting-parsers';

export const useAccountingApiV1 = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function listAccounts(
    includeDeleted?: boolean,
    accountType?: GeneralLedgerAccountType,
  ): Promise<GeneralLedgerAccount[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/accounting/accounts`,
      headers: {
        'X-Tenant': tenant,
      },
      params: {
        includeDeleted,
        accountType,
      },
    };
    return handleRequest<GeneralLedgerAccount[]>(config);
  }

  async function createAccount(req: GeneralLedgerAccountRequest): Promise<GeneralLedgerAccount> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/accounting/accounts`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
    return handleRequest<GeneralLedgerAccount>(config);
  }

  async function getAccount(accountId: string): Promise<GeneralLedgerAccount> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/accounting/accounts/${accountId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<GeneralLedgerAccount>(config);
  }

  async function updateAccount(accountId: string, req: GeneralLedgerAccountRequest): Promise<GeneralLedgerAccount> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/accounting/accounts/${accountId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
    return handleRequest<GeneralLedgerAccount>(config);
  }

  async function deleteAccount(accountId: string): Promise<GeneralLedgerAccount> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/accounting/accounts/${accountId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<GeneralLedgerAccount>(config);
  }

  async function getItemGeneralLedgerAccount(
    itemType?: ItemType,
    itemId?: string,
    transactionType?: ItemGeneralLedgerTransactionType,
  ): Promise<ItemGeneralLedgerAccount | undefined> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/accounting/getItemGeneralLedgerAccount`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        itemType,
        itemId,
        transactionType,
      },
    };
    const result = await handleRequest<ItemGeneralLedgerAccount | undefined>(config);
    return result;
  }

  async function upsertItemGeneralLedgerAccount(
    request: UpsertItemGeneralLedgerAccountRequest & { reassignExistingItems?: boolean },
  ): Promise<ItemGeneralLedgerAccount> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/accounting/upsertItemGeneralLedgerAccount`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const result = await handleRequest<ItemGeneralLedgerAccount>(config);
    return result;
  }

  function accountTotalsHelper(req: AccountTotalsRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v1/accounting/accounts/totals`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  async function accountTotals(req: AccountTotalsRequest): Promise<AccountTotalsRow[]> {
    const config = accountTotalsHelper(req);

    return await handleRequest<AccountTotalsRow[]>(config);
  }

  async function exportAccountTotals(req: AccountTotalsRequest): Promise<BlobPart> {
    const config = accountTotalsHelper(req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  async function getAvailableAccountTotalsRange(): Promise<DateTimeRange> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/accounting/accounts/availableTotalsRange`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const raw = await handleRequest<{ from: string; to: string }>(config);

    return {
      from: DateTime.fromISO(raw.from, { setZone: true }),
      to: DateTime.fromISO(raw.to, { setZone: true }),
    };
  }

  function accountLedgerHelper(accountId: string, req: AccountLedgerRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v1/accounting/accounts/${accountId}/ledger`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  async function accountLedger(accountId: string, req: AccountLedgerRequest): Promise<Pagination<AccountLedgerEntry>> {
    const config = accountLedgerHelper(accountId, req);

    return await handlePaginatedRequest<AccountLedgerEntry>(config);
  }

  async function exportAccountLedger(accountId: string, req: AccountLedgerRequest): Promise<BlobPart> {
    const config = accountLedgerHelper(accountId, req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  async function getAvailableAccountLedgerRange(
    accountId: string,
    req: AvailableAccountLedgerRangeRequest,
  ): Promise<DateTimeRange> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/accounting/accounts/${accountId}/availableLedgerRange`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    const raw = await handleRequest<{ from: string; to: string }>(config);

    return {
      from: DateTime.fromISO(raw.from, { setZone: true }),
      to: DateTime.fromISO(raw.to, { setZone: true }),
    };
  }

  async function getPeriodicLedgerTotals(
    accountId: string,
    req: PeriodicLedgerTotalsRequest,
  ): Promise<PeriodicLedgerTotal[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/accounting/accounts/${accountId}/periodicLedgerTotals`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    const raw = await handleRequest<{ periodStart: string; total: number }[]>(config);

    return raw.map((r) => {
      return {
        periodStart: DateTime.fromISO(r.periodStart, { setZone: true }),
        total: r.total,
      };
    });
  }

  async function listAccountingPeriodCloses(): Promise<AccountingPeriodClose[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/accounting/accountingPeriodCloses`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const raw = await handleRequest<AccountingParsers.AccountingPeriodCloseJson[]>(config);

    return raw.map((json) => AccountingParsers.toAccountingPeriodClose(json));
  }

  async function createAccountingPeriodClose(req: CreateAccountingPeriodCloseRequest): Promise<AccountingPeriodClose> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/accounting/accountingPeriodCloses`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    const raw = await handleRequest<AccountingParsers.AccountingPeriodCloseJson>(config);

    return AccountingParsers.toAccountingPeriodClose(raw);
  }

  async function getCurrentAccountingPeriodClose(): Promise<AccountingPeriodClose | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/accounting/accountingPeriodCloses/current`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const raw = await handleRequest<AccountingParsers.AccountingPeriodCloseJson | undefined>(config);

    return raw && AccountingParsers.toAccountingPeriodClose(raw);
  }

  async function deleteAccountingPeriodClose(id: string): Promise<AccountingPeriodClose> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/accounting/accountingPeriodCloses/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const raw = await handleRequest<AccountingParsers.AccountingPeriodCloseJson>(config);

    return AccountingParsers.toAccountingPeriodClose(raw);
  }

  return {
    accountTotals,
    exportAccountTotals,
    getAvailableAccountTotalsRange,
    accountLedger,
    exportAccountLedger,
    getPeriodicLedgerTotals,
    getAvailableAccountLedgerRange,
    listAccounts,
    createAccount,
    getAccount,
    updateAccount,
    deleteAccount,
    getItemGeneralLedgerAccount,
    upsertItemGeneralLedgerAccount,
    listAccountingPeriodCloses,
    createAccountingPeriodClose,
    deleteAccountingPeriodClose,
    getCurrentAccountingPeriodClose,
  };
};
